import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, Users, Plus, Mail, Check, X, UserPlus, Clock, 
  ChevronRight, Shield, Eye, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { useSwipeToClose } from "../hooks/useSwipeToClose";
import { API } from "../utils/constants";

export const CoCoachPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, loading: userLoading } = useUser();
  
  const [collaborations, setCollaborations] = useState({
    incoming_pending: [],
    outgoing_pending: [],
    accepted_as_head: [],
    accepted_as_co_coach: []
  });
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const inviteModalSwipe = useSwipeToClose(() => setShowInviteModal(false));

  // Fetch collaborations
  useEffect(() => {
    const fetchCollaborations = async () => {
      if (!user?.id) return;
      
      try {
        const res = await axios.get(`${API}/coach/co-coach/collaborations?coach_id=${user.id}`);
        setCollaborations(res.data);
      } catch (e) {
        console.error("Error fetching collaborations:", e);
        toast.error("Failed to load collaborations");
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading && user) {
      fetchCollaborations();
    }
  }, [user, userLoading]);

  const handleInviteCoach = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter coach's email");
      return;
    }

    setInviting(true);
    try {
      await axios.post(`${API}/coach/co-coach/invite-by-email`, {
        head_coach_id: user.id,
        co_coach_email: inviteEmail.trim()
      });
      toast.success("Invitation sent!");
      setShowInviteModal(false);
      setInviteEmail("");
      
      // Refresh collaborations
      const res = await axios.get(`${API}/coach/co-coach/collaborations?coach_id=${user.id}`);
      setCollaborations(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleAcceptInvitation = async (collaborationId) => {
    try {
      await axios.put(`${API}/coach/co-coach/collaborations/${collaborationId}/accept?coach_id=${user.id}`);
      toast.success("Invitation accepted!");
      
      // Refresh collaborations
      const res = await axios.get(`${API}/coach/co-coach/collaborations?coach_id=${user.id}`);
      setCollaborations(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept invitation");
    }
  };

  const handleDeclineInvitation = async (collaborationId) => {
    try {
      await axios.delete(`${API}/coach/co-coach/collaborations/${collaborationId}?coach_id=${user.id}`);
      toast.success("Invitation declined");
      
      // Refresh collaborations
      const res = await axios.get(`${API}/coach/co-coach/collaborations?coach_id=${user.id}`);
      setCollaborations(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to decline invitation");
    }
  };

  const handleRemoveCollaboration = async (collaborationId, name) => {
    if (!window.confirm(`Remove collaboration with ${name}?`)) return;
    
    try {
      await axios.delete(`${API}/coach/co-coach/collaborations/${collaborationId}?coach_id=${user.id}`);
      toast.success("Collaboration removed");
      
      // Refresh collaborations
      const res = await axios.get(`${API}/coach/co-coach/collaborations?coach_id=${user.id}`);
      setCollaborations(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove collaboration");
    }
  };

  const totalIncoming = collaborations.incoming_pending.length;
  const totalOutgoing = collaborations.outgoing_pending.length;
  const totalAcceptedAsHead = collaborations.accepted_as_head.length;
  const totalAcceptedAsCo = collaborations.accepted_as_co_coach.length;

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-co-coach">
      {/* Header */}
      <header className="flex items-center justify-between p-4 pt-6">
        <button 
          data-testid="back-button" 
          onClick={() => navigate(-1)} 
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}
        >
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>Co-Coach Management</h1>
        <button
          data-testid="invite-co-coach-button"
          onClick={() => setShowInviteModal(true)}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-r from-[#493986] to-[#92a7e8]"
        >
          <Plus className="w-5 h-5 text-white" />
        </button>
      </header>

      <div className="flex-1 px-4 pb-24 overflow-y-auto space-y-6">
        {loading ? (
          <div className={`text-center py-12 ${theme.textMuted}`}>Loading...</div>
        ) : (
          <>
            {/* Pending Incoming Invitations */}
            {totalIncoming > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'}`}>
                    <Clock className={`w-4 h-4 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  </div>
                  <h2 className={`font-semibold ${theme.textPrimary}`}>Pending Invitations ({totalIncoming})</h2>
                </div>
                <div className="space-y-3">
                  {collaborations.incoming_pending.map((collab) => (
                    <div 
                      key={collab.id}
                      data-testid={`incoming-invitation-${collab.id}`}
                      className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-purple-500/20' : 'bg-[#493986]/10'}`}>
                          <span className="text-lg font-bold bg-gradient-to-r from-[#493986] to-[#92a7e8] bg-clip-text text-transparent">
                            {collab.head_coach_name?.charAt(0)?.toUpperCase() || 'C'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold ${theme.textPrimary} truncate`}>{collab.head_coach_name}</p>
                          <p className={`text-xs ${theme.textMuted} truncate`}>{collab.head_coach_email}</p>
                          <p className={`text-xs mt-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                            Wants you as co-coach
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-4">
                        <button
                          data-testid={`accept-invitation-${collab.id}`}
                          onClick={() => handleAcceptInvitation(collab.id)}
                          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-semibold flex items-center justify-center gap-2 shadow-lg"
                        >
                          <Check className="w-4 h-4" />
                          Accept
                        </button>
                        <button
                          data-testid={`decline-invitation-${collab.id}`}
                          onClick={() => handleDeclineInvitation(collab.id)}
                          className={`flex-1 py-3 rounded-xl border-2 ${isDark ? 'border-white/20 text-white/80 hover:bg-white/5' : 'border-[#493986]/30 text-[#493986] hover:bg-[#493986]/5'} font-semibold flex items-center justify-center gap-2`}
                        >
                          <X className="w-4 h-4" />
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Outgoing Pending */}
            {totalOutgoing > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                    <UserPlus className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <h2 className={`font-semibold ${theme.textPrimary}`}>Sent Invitations ({totalOutgoing})</h2>
                </div>
                <div className="space-y-3">
                  {collaborations.outgoing_pending.map((collab) => (
                    <div 
                      key={collab.id}
                      data-testid={`outgoing-invitation-${collab.id}`}
                      className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                          <span className="text-lg font-bold text-blue-500">
                            {collab.co_coach_name?.charAt(0)?.toUpperCase() || 'C'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold ${theme.textPrimary} truncate`}>{collab.co_coach_name}</p>
                          <p className={`text-xs ${theme.textMuted} truncate`}>{collab.co_coach_email}</p>
                          <p className={`text-xs mt-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                            Awaiting response...
                          </p>
                        </div>
                        <button
                          data-testid={`cancel-invitation-${collab.id}`}
                          onClick={() => handleRemoveCollaboration(collab.id, collab.co_coach_name)}
                          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'hover:bg-white/10 bg-white/5' : 'hover:bg-[#493986]/10 bg-[#493986]/5'}`}
                        >
                          <X className={`w-5 h-5 ${theme.textMuted}`} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* My Co-Coaches (I'm the head coach) */}
            {totalAcceptedAsHead > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                    <Users className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  </div>
                  <h2 className={`font-semibold ${theme.textPrimary}`}>My Co-Coaches ({totalAcceptedAsHead})</h2>
                </div>
                <div className="space-y-3">
                  {collaborations.accepted_as_head.map((collab) => (
                    <div 
                      key={collab.id}
                      data-testid={`my-co-coach-${collab.id}`}
                      className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                          <span className="text-lg font-bold text-green-500">
                            {collab.co_coach_name?.charAt(0)?.toUpperCase() || 'C'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold ${theme.textPrimary} truncate`}>{collab.co_coach_name}</p>
                          <p className={`text-xs ${theme.textMuted} truncate`}>{collab.co_coach_email}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Eye className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                            <span className={`text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                              {collab.assigned_group_count || 0} group{(collab.assigned_group_count || 0) !== 1 ? 's' : ''} assigned
                            </span>
                          </div>
                        </div>
                        <button
                          data-testid={`remove-co-coach-${collab.id}`}
                          onClick={() => handleRemoveCollaboration(collab.id, collab.co_coach_name)}
                          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'hover:bg-red-500/20 bg-red-500/10' : 'hover:bg-red-100 bg-red-50'}`}
                        >
                          <Trash2 className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
                        </button>
                      </div>
                      {collab.assigned_groups && collab.assigned_groups.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {collab.assigned_groups.map((group) => (
                            <span
                              key={group.id}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white shadow-sm"
                              style={{ backgroundColor: group.color || '#8b5cf6' }}
                            >
                              {group.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Teams I'm Co-Coach of */}
            {totalAcceptedAsCo > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                    <Shield className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <h2 className={`font-semibold ${theme.textPrimary}`}>Teams I Assist ({totalAcceptedAsCo})</h2>
                </div>
                <div className="space-y-3">
                  {collaborations.accepted_as_co_coach.map((collab) => (
                    <div 
                      key={collab.id}
                      data-testid={`assisting-team-${collab.id}`}
                      className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                          <span className="text-lg font-bold bg-gradient-to-r from-[#493986] to-[#92a7e8] bg-clip-text text-transparent">
                            {collab.head_coach_name?.charAt(0)?.toUpperCase() || 'C'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold ${theme.textPrimary} truncate`}>{collab.head_coach_name}'s Team</p>
                          <p className={`text-xs ${theme.textMuted} truncate`}>{collab.head_coach_email}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Eye className={`w-3 h-3 flex-shrink-0 ${theme.textMuted}`} />
                            <span className={`text-xs ${theme.textMuted}`}>
                              Read-only access to {collab.assigned_group_count || 0} group{(collab.assigned_group_count || 0) !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 flex-shrink-0 ${theme.textMuted}`} />
                      </div>
                      {collab.assigned_groups && collab.assigned_groups.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {collab.assigned_groups.map((group) => (
                            <span
                              key={group.id}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white shadow-sm"
                              style={{ backgroundColor: group.color || '#8b5cf6' }}
                            >
                              {group.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {totalIncoming === 0 && totalOutgoing === 0 && totalAcceptedAsHead === 0 && totalAcceptedAsCo === 0 && (
              <div className={`text-center py-12`}>
                <Users className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} />
                <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>No Co-Coach Connections</h3>
                <p className={`text-sm mb-4 ${theme.textMuted}`}>
                  Invite other coaches to collaborate or wait for invitations from head coaches.
                </p>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl"
                >
                  Invite a Co-Coach
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowInviteModal(false)} />
          <div 
            className={`relative w-full max-h-[85vh] overflow-y-auto rounded-t-3xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4`}
            style={{ 
              transform: `translateY(${inviteModalSwipe.translateY}px)`,
              transition: inviteModalSwipe.isDragging ? 'none' : 'transform 0.2s ease-out',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 20px) + 80px)'
            }}
            onTouchStart={inviteModalSwipe.onTouchStart}
            onTouchMove={inviteModalSwipe.onTouchMove}
            onTouchEnd={inviteModalSwipe.onTouchEnd}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 cursor-grab" />
            <h2 className={`text-xl font-bold mb-2 ${theme.textPrimary}`}>Invite Co-Coach</h2>
            <p className={`text-sm mb-6 ${theme.textMuted}`}>
              Enter the email of another coach to invite them as your co-coach. They will get read-only access to groups you assign them to.
            </p>
            
            {/* Email Input */}
            <div className="mb-6">
              <label className={`block text-sm font-medium mb-2 ${theme.textSecondary}`}>Coach Email</label>
              <div className="relative">
                <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${theme.textMuted}`} />
                <input
                  type="email"
                  placeholder="coach@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className={`w-full pl-12 pr-4 py-3 rounded-xl border ${theme.input}`}
                  data-testid="co-coach-email-input"
                />
              </div>
            </div>

            {/* Send Button */}
            <button
              onClick={handleInviteCoach}
              disabled={inviting || !inviteEmail.trim()}
              className="w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl disabled:opacity-50 shadow-lg"
              data-testid="submit-invite-co-coach"
            >
              {inviting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoCoachPage;
